import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  InputAdornment,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccountBalance as BankIcon,
  AttachFile as FileIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrencySymbol, getCurrencyOptions, DEFAULT_CURRENCY, formatCurrency } from '../../utils/currency';

const countryList = require('country-list');

const NewFraudRecord = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form data state
  const [formData, setFormData] = useState({
    callDetails: {
      scamType: '',
      totalAmount: '',
      currency: DEFAULT_CURRENCY,
      description: '',
      callType: 'incoming-call',
      callDate: new Date().toISOString().split('T')[0]
    },
    bankAccounts: [],
    tags: [],
    driveLinks: [] // New field for Google Drive links
  });

  // File upload state (hidden but kept for future use)
  const [evidenceFiles, setEvidenceFiles] = useState([]);

  // Bank account form state
  const [bankAccountForm, setBankAccountForm] = useState({
    accountType: 'UK',
    bankName: '',
    accountHolderName: '',
    sortCode: '',
    accountNumber: '',
    iban: '',
    routingNumber: '',
    bankAccountNumber: '',
    amount: '',
    currency: DEFAULT_CURRENCY
  });

  // Drive link form state
  const [driveLinkForm, setDriveLinkForm] = useState({
    title: '',
    url: '',
    isPublic: false
  });

  // Call type options
  const callTypeOptions = [
    { value: 'incoming-call', label: 'Incoming call' },
    { value: 'text-message', label: 'Text message' },
    { value: 'social-media', label: 'Social media' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'other', label: 'Other' }
  ];

  // State for custom call type
  const [customCallType, setCustomCallType] = useState('');

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleBankAccountChange = (field, value) => {
    // Auto-format UK sort code
    if (field === 'sortCode') {
      // Remove any non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      
      // Limit to 6 digits
      const limited = digitsOnly.slice(0, 6);
      
      // Format as XX-XX-XX
      let formatted = limited;
      if (limited.length > 2) {
        formatted = limited.slice(0, 2) + '-' + limited.slice(2);
      }
      if (limited.length > 4) {
        formatted = limited.slice(0, 2) + '-' + limited.slice(2, 4) + '-' + limited.slice(4);
      }
      
      value = formatted;
    }
    
    setBankAccountForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDriveLinkChange = (field, value) => {
    setDriveLinkForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateBankAccount = (account) => {
    // Required fields
    if (!account.bankName || !account.accountHolderName) {
      return 'Please fill in bank name and account holder name';
    }

    // UK validation
    if (account.accountType === 'UK') {
      if (!account.sortCode) {
        return 'Sort code is required for UK accounts';
      }
      if (!/^\d{2}-\d{2}-\d{2}$/.test(account.sortCode)) {
        return 'Sort code must be in format XX-XX-XX (6 digits)';
      }
      if (!account.accountNumber) {
        return 'Account number is required for UK accounts';
      }
      if (!/^\d{8}$/.test(account.accountNumber)) {
        return 'Account number must be 8 digits for UK accounts';
      }
    }

    // US validation
    if (account.accountType === 'US') {
      if (!account.routingNumber) {
        return 'Routing number is required for US accounts';
      }
      if (!/^\d{9}$/.test(account.routingNumber)) {
        return 'Routing number must be 9 digits for US accounts';
      }
      if (!account.bankAccountNumber) {
        return 'Account number is required for US accounts';
      }
      if (!/^\d{4,17}$/.test(account.bankAccountNumber)) {
        return 'Account number must be 4-17 digits for US accounts';
      }
    }

    // EU/IBAN validation
    if (account.accountType === 'EU') {
      if (!account.iban) {
        return 'IBAN is required for EU accounts';
      }
      if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/.test(account.iban.toUpperCase().replace(/\s/g, ''))) {
        return 'Invalid IBAN format';
      }
    }

    return null; // Valid
  };

  const addBankAccount = () => {
    const validationError = validateBankAccount(bankAccountForm);
    if (validationError) {
      setError(validationError);
      return;
    }

    const newAccount = {
      ...bankAccountForm,
      tempId: Date.now(), // Use tempId instead of id
      amount: parseFloat(bankAccountForm.amount) || 0
    };

    setFormData(prev => ({
      ...prev,
      bankAccounts: [...prev.bankAccounts, newAccount]
    }));

    // Reset form
    setBankAccountForm({
      accountType: 'UK',
      bankName: '',
      accountHolderName: '',
      sortCode: '',
      accountNumber: '',
      iban: '',
      routingNumber: '',
      bankAccountNumber: '',
      amount: '',
      currency: DEFAULT_CURRENCY
    });
    setError(''); // Clear error on success
  };

  const removeBankAccount = (tempId) => {
    setFormData(prev => ({
      ...prev,
      bankAccounts: prev.bankAccounts.filter(account => account.tempId !== tempId)
    }));
  };

  const addDriveLink = () => {
    if (!driveLinkForm.title || !driveLinkForm.url) {
      setError('Please fill in both title and URL for the drive link');
      return;
    }

    if (!driveLinkForm.isPublic) {
      setError('Please confirm that the Google Drive link is set to "Anyone with the link can view"');
      return;
    }

    // Basic URL validation
    try {
      new URL(driveLinkForm.url);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    const newLink = {
      title: driveLinkForm.title,
      url: driveLinkForm.url,
      tempId: Date.now()
    };

    setFormData(prev => ({
      ...prev,
      driveLinks: [...prev.driveLinks, newLink]
    }));

    // Reset form
    setDriveLinkForm({
      title: '',
      url: '',
      isPublic: false
    });
  };

  const removeDriveLink = (tempId) => {
    setFormData(prev => ({
      ...prev,
      driveLinks: prev.driveLinks.filter(link => link.tempId !== tempId)
    }));
  };

  // File upload handlers (hidden but kept for future use)
  const onDropEvidence = useCallback((acceptedFiles) => {
    setEvidenceFiles(prev => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps: getEvidenceRootProps, getInputProps: getEvidenceInputProps, isDragActive: isEvidenceDragActive } = useDropzone({
    onDrop: onDropEvidence,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.aac', '.ogg'],
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp'],
      'video/*': ['.mp4', '.avi', '.mov', '.wmv', '.webm'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    multiple: true
  });

  const removeEvidenceFile = (index) => {
    setEvidenceFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileTypeIcon = (file) => {
    if (file.type?.startsWith('audio/')) return '🎵';
    if (file.type?.startsWith('image/')) return '🖼️';
    if (file.type?.startsWith('video/')) return '🎥';
    if (file.type === 'application/pdf') return '📄';
    if (file.type?.includes('document') || file.type?.includes('word')) return '📝';
    if (file.type?.includes('spreadsheet') || file.type?.includes('excel')) return '📊';
    if (file.type?.includes('text')) return '📋';
    return '📎';
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate required fields
      if (!formData.callDetails.scamType || !formData.callDetails.description) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Auto-add drive link if filled but not clicked "Add Link"
      let finalDriveLinks = [...formData.driveLinks];
      if (driveLinkForm.title && driveLinkForm.url) {
        // Validate the pending drive link
        if (!driveLinkForm.isPublic) {
          setError('Please confirm that the Google Drive link is set to "Anyone with the link can view"');
          setLoading(false);
          return;
        }
        try {
          new URL(driveLinkForm.url);
          finalDriveLinks.push({
            title: driveLinkForm.title,
            url: driveLinkForm.url
          });
        } catch {
          setError('Please enter a valid URL for the drive link');
          setLoading(false);
          return;
        }
      }

      // Auto-add bank account if filled but not clicked "Add Account"
      let finalBankAccounts = [...formData.bankAccounts];
      if (bankAccountForm.bankName && bankAccountForm.accountHolderName) {
        finalBankAccounts.push({
          ...bankAccountForm,
          amount: parseFloat(bankAccountForm.amount) || 0
        });
      }

      // Prepare form data
      const formDataToSend = new FormData();
      
      // Add form data
      const dataToSend = {
        ...formData,
        bankAccounts: finalBankAccounts.map(account => {
          const { tempId, ...accountData } = account; // Remove tempId before sending
          return accountData;
        }),
        driveLinks: finalDriveLinks.map(link => {
          const { tempId, ...linkData } = link; // Remove tempId before sending
          return linkData;
        })
      };
      
      formDataToSend.append('data', JSON.stringify(dataToSend));

      // Add files (hidden functionality)
      evidenceFiles.forEach((file) => {
        formDataToSend.append('evidenceFiles', file);
      });

      const response = await axios.post('/api/fraud-records', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setSuccess('Fraud record created successfully!');
      setTimeout(() => {
        navigate('/fraud-records');
      }, 2000);

    } catch (err) {
      console.error('Error creating fraud record:', err);
      setError(err.response?.data?.message || 'Failed to create fraud record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/fraud-records')}
          sx={{ color: '#b0b0b0', mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700 }}>
          New Fraud Record
        </Typography>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, bgcolor: '#2a1a1a', color: '#ff6b6b' }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3, bgcolor: '#1a2a1a', color: '#51cf66' }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Call Details */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #2a2a2a', mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 3 }}>
                Call Details
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Scam Type *"
                    value={formData.callDetails.scamType}
                    onChange={(e) => handleInputChange('callDetails', 'scamType', e.target.value)}
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumericFormat
                    customInput={TextField}
                    fullWidth
                    label="Total Amount *"
                    value={formData.callDetails.totalAmount}
                    onValueChange={(values) => handleInputChange('callDetails', 'totalAmount', values.value)}
                    thousandSeparator=","
                    prefix="$"
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#bdbdbd' }}>Currency</InputLabel>
                    <Select
                      value={formData.callDetails.currency}
                      onChange={(e) => handleInputChange('callDetails', 'currency', e.target.value)}
                      sx={{ color: '#ffffff', bgcolor: '#2a2a2a' }}
                    >
                      {getCurrencyOptions().map((currency) => (
                        <MenuItem key={currency.value} value={currency.value}>
                          {currency.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description *"
                    multiline
                    rows={4}
                    value={formData.callDetails.description}
                    onChange={(e) => handleInputChange('callDetails', 'description', e.target.value)}
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#bdbdbd' }}>Contact Type</InputLabel>
                    <Select
                      value={
                        customCallType 
                          ? 'other' 
                          : callTypeOptions.find(opt => opt.value === formData.callDetails.callType)
                            ? formData.callDetails.callType
                            : 'other'
                      }
                      onChange={(e) => {
                        if (e.target.value !== 'other') {
                          setCustomCallType('');
                          handleInputChange('callDetails', 'callType', e.target.value);
                        } else {
                          handleInputChange('callDetails', 'callType', 'other');
                        }
                      }}
                      sx={{ color: '#ffffff', bgcolor: '#2a2a2a' }}
                    >
                      {callTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {(customCallType || formData.callDetails.callType === 'other' || 
                  !callTypeOptions.find(opt => opt.value === formData.callDetails.callType)) && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Custom Contact Type"
                      placeholder="Enter custom contact type"
                      value={customCallType || (
                        !callTypeOptions.find(opt => opt.value === formData.callDetails.callType) 
                          ? formData.callDetails.callType 
                          : ''
                      )}
                      onChange={(e) => {
                        setCustomCallType(e.target.value);
                        handleInputChange('callDetails', 'callType', e.target.value || 'other');
                      }}
                      InputProps={{ sx: { color: '#ffffff' } }}
                      InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Date"
                    type="date"
                    value={formData.callDetails.callDate}
                    onChange={(e) => handleInputChange('callDetails', 'callDate', e.target.value)}
                    InputLabelProps={{ shrink: true, sx: { color: '#bdbdbd' } }}
                    InputProps={{ sx: { color: '#ffffff' } }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Bank Accounts */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #2a2a2a', mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 3, display: 'flex', alignItems: 'center' }}>
                <BankIcon sx={{ mr: 1 }} /> Bank Accounts
              </Typography>

              {/* Bank Account Form */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#bdbdbd' }}>Account Type</InputLabel>
                    <Select
                      value={bankAccountForm.accountType}
                      onChange={(e) => handleBankAccountChange('accountType', e.target.value)}
                      sx={{ color: '#ffffff', bgcolor: '#2a2a2a' }}
                    >
                      <MenuItem value="UK">UK</MenuItem>
                      <MenuItem value="US">US</MenuItem>
                      <MenuItem value="EU">EU</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bank Name *"
                    value={bankAccountForm.bankName}
                    onChange={(e) => handleBankAccountChange('bankName', e.target.value)}
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Account Holder Name *"
                    value={bankAccountForm.accountHolderName}
                    onChange={(e) => handleBankAccountChange('accountHolderName', e.target.value)}
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                {bankAccountForm.accountType === 'UK' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Sort Code"
                        placeholder="12-34-56 or 123456"
                        value={bankAccountForm.sortCode}
                        onChange={(e) => handleBankAccountChange('sortCode', e.target.value)}
                        InputProps={{ sx: { color: '#ffffff' } }}
                        InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                        helperText="Enter 6 digits (auto-formatted)"
                        FormHelperTextProps={{ sx: { color: '#888' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account Number"
                        value={bankAccountForm.accountNumber}
                        onChange={(e) => handleBankAccountChange('accountNumber', e.target.value)}
                        InputProps={{ sx: { color: '#ffffff' } }}
                        InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                      />
                    </Grid>
                  </>
                )}
                {bankAccountForm.accountType === 'US' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Routing Number"
                        value={bankAccountForm.routingNumber}
                        onChange={(e) => handleBankAccountChange('routingNumber', e.target.value)}
                        InputProps={{ sx: { color: '#ffffff' } }}
                        InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account Number"
                        value={bankAccountForm.bankAccountNumber}
                        onChange={(e) => handleBankAccountChange('bankAccountNumber', e.target.value)}
                        InputProps={{ sx: { color: '#ffffff' } }}
                        InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                      />
                    </Grid>
                  </>
                )}
                {bankAccountForm.accountType === 'EU' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="IBAN"
                      value={bankAccountForm.iban}
                      onChange={(e) => handleBankAccountChange('iban', e.target.value)}
                      InputProps={{ sx: { color: '#ffffff' } }}
                      InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <NumericFormat
                    customInput={TextField}
                    fullWidth
                    label="Amount"
                    value={bankAccountForm.amount}
                    onValueChange={(values) => handleBankAccountChange('amount', values.value)}
                    thousandSeparator=","
                    prefix="$"
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#bdbdbd' }}>Currency</InputLabel>
                    <Select
                      value={bankAccountForm.currency}
                      onChange={(e) => handleBankAccountChange('currency', e.target.value)}
                      sx={{ color: '#ffffff', bgcolor: '#2a2a2a' }}
                    >
                      {getCurrencyOptions().map((currency) => (
                        <MenuItem key={currency.value} value={currency.value}>
                          {currency.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={addBankAccount}
                    startIcon={<AddIcon />}
                    sx={{ borderColor: '#6464ff', color: '#6464ff' }}
                  >
                    Add Bank Account
                  </Button>
                </Grid>
              </Grid>

              {/* Bank Accounts List */}
              {formData.bankAccounts.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#b0b0b0', mb: 2 }}>
                    Added Bank Accounts ({formData.bankAccounts.length})
                  </Typography>
                  <List>
                    {formData.bankAccounts.map((account, index) => (
                      <ListItem key={account.tempId} sx={{ bgcolor: '#2a2a2a', mb: 1, borderRadius: 1 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ color: '#ffffff' }}>
                              {account.bankName} - {account.accountHolderName}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: '#b0b0b0' }}>
                              {account.accountType} • {formatCurrency(account.amount, account.currency)}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            onClick={() => removeBankAccount(account.tempId)}
                            sx={{ color: '#c83232' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Google Drive Links */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #2a2a2a', mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 3, display: 'flex', alignItems: 'center' }}>
                <LinkIcon sx={{ mr: 1 }} /> Evidence Links (Google Drive)
              </Typography>

              {/* Drive Link Form */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Link Title *"
                    value={driveLinkForm.title}
                    onChange={(e) => handleDriveLinkChange('title', e.target.value)}
                    placeholder="e.g., Screenshot of conversation"
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Google Drive URL *"
                    value={driveLinkForm.url}
                    onChange={(e) => handleDriveLinkChange('url', e.target.value)}
                    placeholder="https://drive.google.com/file/d/..."
                    InputProps={{ sx: { color: '#ffffff' } }}
                    InputLabelProps={{ sx: { color: '#bdbdbd' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={driveLinkForm.isPublic}
                        onChange={(e) => handleDriveLinkChange('isPublic', e.target.checked)}
                        sx={{ 
                          color: '#6464ff',
                          '&.Mui-checked': { color: '#6464ff' }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: '#ffffff' }}>
                        I confirm that this Google Drive link is set to "Anyone with the link can view"
                      </Typography>
                    }
                    sx={{ 
                      alignItems: 'flex-start',
                      '& .MuiFormControlLabel-label': { color: '#ffffff' }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={addDriveLink}
                    startIcon={<AddIcon />}
                    sx={{ borderColor: '#6464ff', color: '#6464ff' }}
                  >
                    Add Drive Link
                  </Button>
                </Grid>
              </Grid>

              {/* Drive Links List */}
              {formData.driveLinks.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#b0b0b0', mb: 2 }}>
                    Added Drive Links ({formData.driveLinks.length})
                  </Typography>
                  <List>
                    {formData.driveLinks.map((link, index) => (
                      <ListItem key={link.tempId} sx={{ bgcolor: '#2a2a2a', mb: 1, borderRadius: 1 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ color: '#ffffff' }}>
                              {link.title}
                            </Typography>
                          }
                          secondary={
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#6464ff', 
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                '&:hover': { color: '#8484ff' }
                              }}
                              onClick={() => window.open(link.url, '_blank')}
                            >
                              {link.url}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            onClick={() => removeDriveLink(link.tempId)}
                            sx={{ color: '#c83232' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Hidden File Upload Section (kept for future use) */}
              <Box sx={{ display: 'none' }}>
                <Paper
                  {...getEvidenceRootProps()}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    bgcolor: isEvidenceDragActive ? '#2a2a2a' : '#1a1a1a',
                    border: `2px dashed ${isEvidenceDragActive ? '#6464ff' : '#444'}`,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#6464ff',
                      bgcolor: '#2a2a2a'
                    }
                  }}
                >
                  <input {...getEvidenceInputProps()} />
                  <UploadIcon sx={{ fontSize: 48, color: '#666', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                    Drop evidence files here
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#b0b0b0', mb: 2 }}>
                    Supports: Audio, Images, Videos, Documents (PDF, DOC, XLS, etc.)
                  </Typography>
                  <Button variant="outlined" sx={{ borderColor: '#6464ff', color: '#6464ff' }}>
                    Browse Files
                  </Button>
                </Paper>

                {/* Evidence Files List */}
                {evidenceFiles.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: '#b0b0b0', mb: 2 }}>
                      Evidence Files ({evidenceFiles.length})
                    </Typography>
                    <List>
                      {evidenceFiles.map((file, index) => (
                        <ListItem key={index} sx={{ bgcolor: '#2a2a2a', mb: 1, borderRadius: 1 }}>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ color: '#ffffff', display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: 8 }}>{getFileTypeIcon(file)}</span>
                                {file.name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" sx={{ color: '#b0b0b0' }}>
                                {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type || 'Unknown type'}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              onClick={() => removeEvidenceFile(index)}
                              sx={{ color: '#c83232' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Submit Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          endIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          sx={{
            background: 'linear-gradient(135deg, #6464ff, #8484ff)',
            color: '#ffffff',
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            '&:hover': {
              background: 'linear-gradient(135deg, #5454ff, #7474ff)',
            },
            '&:disabled': {
              background: '#333',
              color: '#666'
            }
          }}
        >
          {loading ? 'Creating Case...' : 'Create Fraud Case'}
        </Button>
      </Box>
    </Box>
  );
};

export default NewFraudRecord;
